import { defineStore } from 'pinia'

import { apiClientDB } from '@/config_sessions'
import { useZoneDirectoryStore } from '@/stores/ZoneDirectory'
import { useChangesTodayStore } from '@/stores/ChangesToday'
import { CAMERA_MOVED_LABEL } from '@/zones/changeQuestion'

// A stored zone row as the payload supersede takes (its own drawing, unchanged).
const _parsed = (v) => {
    if (v === null || v === undefined || typeof v !== 'string') return v ?? null
    try { return JSON.parse(v) } catch { return null }
}
const asZonePayload = (z) => ({
    DetectionZoneName: z.DetectionZoneName,
    DetectionZoneTypeId: z.DetectionZoneTypeId,
    DetectionZonePointArray: _parsed(z.DetectionZonePointArray),
    DetectionZoneTripwirePoints: _parsed(z.DetectionZoneTripwirePoints),
    DetectionZoneSettings: _parsed(z.DetectionZoneSettings) || {},
})

// ---------- Tripwire shape helpers -----------------------------------------
//
// DetectionZoneTripwirePoints can appear in three JSON shapes:
//   Current (groups with IDs):   [{"id":"tw_…","points":[[x,y],[x,y]]}, …]
//   Intermediate (groups, bare): [[[x,y],[x,y]], …]
//   Legacy (single tripwire):    [[x,y],[x,y]]
// The store always emits the current shape and normalizes both older shapes
// when reading. Diff keys strip IDs so a no-op edit doesn't churn just because
// the client regenerated identifiers.

const _isPoint = (p) =>
    Array.isArray(p) && p.length === 2 &&
    typeof p[0] === 'number' && typeof p[1] === 'number'

const _isTripwire = (tw) =>
    Array.isArray(tw) && tw.length === 2 && _isPoint(tw[0]) && _isPoint(tw[1])

const _isTripwireObject = (o) =>
    o && typeof o === 'object' &&
    typeof o.id === 'string' && o.id.length > 0 &&
    _isTripwire(o.points)

// Stable per-tripwire identifier. The keyspace for `TripwireDirections` in
// DetectionZoneSettings lives off these — they have to survive add / delete
// / reorder operations cleanly. Frontend-generated; backend preserves them.
export const generateTripwireId = () =>
    `tw_${Math.random().toString(16).slice(2, 10).padStart(8, '0')}`

// Normalize any storage shape to a canonical list of {id, points} objects.
// Returns null when the input does not describe at least one valid tripwire.
// IDs are synthesized for entries that arrive without one.
const normalizeStoredTripwires = (g) => {
    if (g === null || g === undefined) return null
    if (!Array.isArray(g) || g.length === 0) return null
    // Legacy single-tripwire shape: [[x,y],[x,y]] is itself one tripwire.
    if (_isTripwire(g)) return [{ id: generateTripwireId(), points: g }]
    const out = []
    for (const item of g) {
        if (_isTripwireObject(item)) {
            out.push({ id: item.id, points: item.points })
        } else if (_isTripwire(item)) {
            out.push({ id: generateTripwireId(), points: item })
        }
    }
    return out.length > 0 ? out : null
}

// For diff keys: strip IDs so two equivalent geometries compare equal even
// when one side has synthesized fresh IDs. Returns a sorted-stable form of
// the geometry — the array order matters (it's the order in which tripwires
// are visible to the user), but identifiers are noise.
const geometryOnlyForDiff = (tripwires) => {
    if (!tripwires) return null
    return tripwires.map(tw => tw.points)
}

// Read the tripwires off a zone payload (as emitted by DetectionZone.vue).
// Prefers the new `tripwires` array (which carries {id, points}); falls back
// to the legacy `tripwire` object when the array is absent. Returns an array
// of {id, points} entries — possibly empty, never null. Any entry missing an
// id gets one synthesized so the storage shape is always uniform.
const extractTripwiresFromZonePayload = (zone) => {
    if (zone && Array.isArray(zone.tripwires)) {
        return zone.tripwires
            .filter(tw => tw && _isTripwire(tw.points))
            .map(tw => ({
                id: (typeof tw.id === 'string' && tw.id) ? tw.id : generateTripwireId(),
                points: tw.points,
            }))
    }
    if (zone && zone.tripwire && _isTripwire(zone.tripwire.points)) {
        const id = (typeof zone.tripwire.id === 'string' && zone.tripwire.id)
            ? zone.tripwire.id
            : generateTripwireId()
        return [{ id, points: zone.tripwire.points }]
    }
    return []
}

export const useDetectionZoneStore = defineStore({
    id: 'detectionzones',

    state: () => ({
        detectionzones: [],
        detectionzone: {},
        loading: false,
        error: null
    }),

    getters: {

    },

    actions: {
        async fetchDetectionZones() {
            this.detectionzones = []
            this.loading = true
            try {
                const response = await apiClientDB.get(`/detection_zones/`, {
                    headers: {
                        'Authorization': `Bearer ${localStorage.getItem('authToken')}`
                    }
                })
                // Set detectionzones with fetched data
                this.detectionzones = response.data.detectionzones

                // Cache the response data in local storage for future use
                localStorage.setItem('detectionZoneData', JSON.stringify(this.detectionzones))

                return this.detectionzones
            }
            catch (error) {
                this.error = error
            }
            finally {
                this.loading = false
            }
        },

        async fetchDetectionZone(DetectionZoneId) {
            this.detectionzone = {}
            this.loading = true
            try {
                const response = await apiClientDB.get(`/detection_zones/${DetectionZoneId}/`, {
                    headers: {
                        'Authorization': `Bearer ${localStorage.getItem('authToken')}`
                    }
                })
                this.detectionzone = response.data  
            }
            catch (error) {
                this.error = error
            }
            finally {
                this.loading = false
            }
        },

        async addDetectionZoneToFlow(FlowId, DetectionZone) {
            try {
                const data = {
                    FlowId,
                    DetectionZone
                }

                await apiClientDB.post(`/detection_zones/`, data, {
                    headers: {
                        'Authorization': `Bearer ${localStorage.getItem('authToken')}`
                    }
                })

                // Clear or update local storage to ensure fresh data
                localStorage.removeItem('detectionZoneData')
                localStorage.removeItem('FixedWidgetsData')

                await this.fetchDetectionZones()
            }
            catch (error) {
                if (error.response) {
                    alert("There was an error processing your request. You will be redirected to the homepage.")
                    setTimeout(() => {
                        window.location = '/'
                    }, 3000)
                } 
                else {
                    this.error = error
                }
            }
            finally {
                this.loading = false
            }
        },

        // `askChangeKinds` (optional): async ([{ oldId, oldZone, zone }]) => { [oldId]: kind }
        // or null to cancel — the editor's "what happened at this spot?" question for every
        // zone this save replaces (corrected | changed | different; settings step 2). It is
        // asked BEFORE anything is written, so cancelling saves nothing.
        // `cameraMoved`: "The camera was moved" (step 2e) — the question also covers every
        // zone on the camera that was NOT redrawn; "changed" gives those a chart marker
        // only, "different" a fresh start with the same drawing.
        async editDetectionZoneToFlow(FlowId, newDetectionZones, { askChangeKinds = null, cameraMoved = false } = {}) {
            try {
                const currentZones = await this.fetchDetectionZonesByFlowId(FlowId)
                const detectionZoneTypes = await this.fetchDetectionZoneTypes()

                // Transform newDetectionZones to an array format.
                // A zone must carry at least one geometry: a polygon (≥3 points) and/or
                // at least one tripwire (each tripwire being a 2-point segment).
                // Absent geometries are represented as null (not [] or empty object) so the diff key
                // matches what the backend returns for the current side of the comparison.
                // Tripwires are always emitted in the canonical multi-tripwire storage shape
                // ([[[x,y],[x,y]], ...]) — even for a single tripwire — so legacy-shape rows
                // in the DB compare cleanly against new-shape rows after normalization.
                const transformedNewZones = Object.keys(newDetectionZones).map(key => {
                    const zone = newDetectionZones[key]
                    if (!zone.type) {
                        throw new Error(`Invalid data for detection zone: ${key} (missing type)`)
                    }
                    const polygonPts = Array.isArray(zone.points) ? zone.points : []
                    const tripwiresList = extractTripwiresFromZonePayload(zone)
                    const hasPoly = polygonPts.length >= 3
                    const hasTrip = tripwiresList.length > 0
                    if (!hasPoly && !hasTrip) {
                        throw new Error(`Invalid data for detection zone: ${key} (no polygon or tripwire)`)
                    }
                    return {
                        // Carried back from /flows: the zone this entry IS, so a rename
                        // is saved as its next version, not as delete + a new zone.
                        DetectionZoneId: zone.DetectionZoneId ?? null,
                        DetectionZoneName: key,
                        DetectionZoneTypeId: detectionZoneTypes.get(zone.type),
                        DetectionZonePointArray: hasPoly ? JSON.parse(JSON.stringify(polygonPts)) : null,
                        DetectionZoneTripwirePoints: hasTrip ? JSON.parse(JSON.stringify(tripwiresList)) : null,
                        DetectionZoneSettings: JSON.parse(JSON.stringify(zone.settings || {})),
                        FlowId: FlowId
                    }
                })
                
                let toAdd = []
                let toDelete = []

                // Perform comparison and update if zones exist
                const comparisonResult = this.compareDetectionZones(currentZones, transformedNewZones)
                toAdd = comparisonResult.toAdd || []
                toDelete = comparisonResult.toDelete || []

                // An edited zone is retired AND re-added. Send that pair as one supersede,
                // so the server rolls it in a single transaction: the new version keeps the
                // zone's identity (ZoneKey) and everything pointing at it follows — on a
                // rename too. Paired by the zone's id; by name for an entry without one.
                const supersedes = []
                for (const del of toDelete) {
                    let idx = toAdd.findIndex(z => z.DetectionZoneId === del.DetectionZoneId)
                    if (idx === -1 && del.reason === 'superseded') {
                        idx = toAdd.findIndex(z => !z.DetectionZoneId
                            && z.DetectionZoneName === del.DetectionZoneName)
                    }
                    if (idx !== -1) {
                        supersedes.push({ oldId: del.DetectionZoneId, zone: toAdd[idx] })
                        toAdd.splice(idx, 1)
                    }
                }
                const supersededIds = new Set(supersedes.map(s => s.oldId))

                // The camera was moved: the zones nobody redrew are asked about too.
                const touchedIds = new Set(toDelete.map(d => d.DetectionZoneId))
                const untouched = cameraMoved ? currentZones.filter(z => !touchedIds.has(z.DetectionZoneId)) : []

                let changeKinds = {}
                if (askChangeKinds && (supersedes.length || untouched.length)) {
                    const byId = new Map(currentZones.map(z => [z.DetectionZoneId, z]))
                    const answers = await askChangeKinds([
                        ...supersedes.map(s => ({ oldId: s.oldId, oldZone: byId.get(s.oldId), zone: s.zone })),
                        ...untouched.map(z => ({ oldId: z.DetectionZoneId, oldZone: z, zone: z })),
                    ])
                    if (!answers) {
                        const cancelled = new Error('Save cancelled')
                        cancelled.cancelled = true
                        throw cancelled
                    }
                    changeKinds = answers
                }

                // Real deletes first: a zone may take over the name of one deleted in the
                // same save, and the server refuses two active zones with one name.
                for (const { DetectionZoneId, reason } of toDelete) {
                    if (!supersededIds.has(DetectionZoneId)) {
                        await this.deleteDetectionZone(DetectionZoneId, reason)
                    }
                }

                const markerLabel = cameraMoved ? CAMERA_MOVED_LABEL : null
                for (const { oldId, zone } of supersedes) {
                    await this.supersedeDetectionZone(oldId, zone, changeKinds[oldId], markerLabel)
                }
                for (const z of untouched) {
                    const kind = changeKinds[z.DetectionZoneId]
                    if (kind === 'changed') await this.addZoneMarker(z.ZoneKey, CAMERA_MOVED_LABEL)
                    else if (kind === 'different') {
                        await this.supersedeDetectionZone(z.DetectionZoneId, asZonePayload(z), 'different')
                    }
                }

                // Add new zones
                for (const zone of toAdd) {
                    await this.addDetectionZone(zone)
                }

                console.log('Detection zones updated successfully.')
            } catch (error) {
                // Surface it: the caller must not report success. Part of the save may
                // already be committed (each call is its own transaction).
                if (!error?.cancelled) console.error("Error updating DetectionZone table:", error)
                throw error
            } finally {
                // Always refresh the shared zone list — also after a partial save — so
                // everything that shows zone names (the side menu among them) matches
                // the database without a page reload. Same for the zone directory.
                await this.fetchDetectionZones()
                const directory = useZoneDirectoryStore()
                if (directory.companyId !== null || directory.zones.length) {
                    await directory.fetch(directory.companyId)
                }
                // A type change was just registered: the zone page and Explore show it now.
                const changes = useChangesTodayStore()
                await changes.fetch(changes.companyId)
            }
        },

        async fetchDetectionZoneTypes() {
            const response = await apiClientDB.get('/detectionzonetypes/', {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('authToken')}`
                }
            })
            const typesArray = response.data.detectionzonetypes
            const typesMap = new Map()
            typesArray.forEach(type => {
                typesMap.set(type.DetectionZoneTypeName, type.DetectionZoneTypeId)
            })
            return typesMap
        },

        compareDetectionZones(currentZones, newZones) {
            const toAdd = []
            const toDelete = []

            // Ensure inputs are arrays
            currentZones = currentZones || []
            newZones = newZones || []

            const safeParse = (str) => {
                if (str === null || str === undefined) return null
                if (typeof str !== 'string') return str
                try { return JSON.parse(str) } catch { return null }
            }

            // An absent geometry is represented as null in the diff key. Empty arrays from
            // either side are normalized to null so "[]" doesn't compare unequal to "null".
            const normalizeGeom = (g) => {
                if (g === null || g === undefined) return null
                if (Array.isArray(g) && g.length === 0) return null
                return g
            }

            // Diff key includes BOTH polygon and tripwire geometries so any geometry change
            // (add/remove/edit polygon or tripwire) is detected as a change. Tripwires are
            // normalized on both sides AND IDs are stripped via geometryOnlyForDiff so the
            // comparison is over geometry alone — synthesizing fresh IDs for legacy-shape
            // rows on one side must not falsely flag the zone as changed. Adding or
            // removing a tripwire still changes the array length and therefore the key.
            const keyOfCurrent = (zone) => {
                const polygon = normalizeGeom(safeParse(zone.DetectionZonePointArray))
                const tripwire = geometryOnlyForDiff(normalizeStoredTripwires(safeParse(zone.DetectionZoneTripwirePoints)))
                return `${zone.DetectionZoneTypeId}-${zone.DetectionZoneName}-${JSON.stringify(polygon)}-${JSON.stringify(tripwire)}`
            }
            const keyOfNew = (zone) => {
                const polygon = normalizeGeom(zone.DetectionZonePointArray)
                const tripwire = geometryOnlyForDiff(normalizeStoredTripwires(zone.DetectionZoneTripwirePoints ?? null))
                return `${zone.DetectionZoneTypeId}-${zone.DetectionZoneName}-${JSON.stringify(polygon)}-${JSON.stringify(tripwire)}`
            }

            // 1) An entry that says which zone it is (DetectionZoneId, carried from
            //    /flows) is compared with THAT zone. Any difference — name, type or
            //    geometry — makes it that zone's next version. Matching by content
            //    instead would mistake a renamed zone for an untouched one whenever it
            //    takes over the name and shape of a zone deleted in the same save.
            const currentById = new Map(currentZones.map(z => [z.DetectionZoneId, z]))
            const claimed = new Set()
            const withoutId = []
            for (const zone of newZones) {
                const cur = zone.DetectionZoneId != null ? currentById.get(zone.DetectionZoneId) : undefined
                if (!cur) {
                    withoutId.push(zone)
                    continue
                }
                claimed.add(cur.DetectionZoneId)
                if (keyOfCurrent(cur) !== keyOfNew(zone)) {
                    toDelete.push({ DetectionZoneId: cur.DetectionZoneId,
                                    DetectionZoneName: cur.DetectionZoneName, reason: 'superseded' })
                    toAdd.push(zone)
                }
            }

            // 2) Entries without an id (new zones, or an older build): by content against
            //    the zones not claimed above. Diff key includes BOTH polygon and tripwire
            //    geometries so any geometry change is detected; tripwire IDs are stripped
            //    via geometryOnlyForDiff so the comparison is over geometry alone.
            const unclaimedByKey = new Map()
            currentZones.forEach(z => {
                if (!claimed.has(z.DetectionZoneId)) unclaimedByKey.set(keyOfCurrent(z), z)
            })
            for (const zone of withoutId) {
                const cur = unclaimedByKey.get(keyOfNew(zone))
                if (cur) claimed.add(cur.DetectionZoneId)
                else toAdd.push(zone)
            }

            // 3) Every zone nobody claimed is retired. One whose name an id-less entry
            //    still carries is being SUPERSEDED by it (that entry is its replacement);
            //    otherwise it was deleted. Only the client can tell the two apart, and
            //    the backend records it on the retired row so the archive can
            //    distinguish "previous version" from "gone".
            const survivingNames = new Set(withoutId.map(z => z.DetectionZoneName))
            currentZones.forEach(z => {
                if (claimed.has(z.DetectionZoneId)) return
                toDelete.push({
                    DetectionZoneId: z.DetectionZoneId,
                    DetectionZoneName: z.DetectionZoneName,
                    reason: survivingNames.has(z.DetectionZoneName) ? 'superseded' : 'deleted'
                })
            })

            return { toAdd, toDelete }
        },
        
        async fetchDetectionZonesByFlowId(FlowId) {
            const response = await apiClientDB.get(`/detection_zones/flow/${FlowId}/`, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('authToken')}`
                }
            })
            return response.data.detectionZones || []
        },
        
        async addDetectionZone(zone) {
            try {
                // A zone must have a name, a type, a flow, and at least one geometry (polygon or tripwire).
                if (!zone.DetectionZoneName || !zone.DetectionZoneTypeId || !zone.FlowId) {
                    throw new Error('Invalid detection zone data')
                }
                const polygonOk = Array.isArray(zone.DetectionZonePointArray) && zone.DetectionZonePointArray.length >= 3
                // Tripwire data may arrive in any of the three shapes:
                //   current     [{id, points}, ...]
                //   intermediate [[[x,y],[x,y]], ...]
                //   legacy       [[x,y],[x,y]]
                // Valid when at least one well-formed tripwire is present.
                const tripwireOk =
                    Array.isArray(zone.DetectionZoneTripwirePoints) &&
                    (_isTripwire(zone.DetectionZoneTripwirePoints) ||
                     zone.DetectionZoneTripwirePoints.some(item =>
                         _isTripwireObject(item) || _isTripwire(item)
                     ))
                if (!polygonOk && !tripwireOk) {
                    throw new Error('Detection zone must have a polygon (≥3 points) or a tripwire (2 points)')
                }

                const data = {
                    FlowId: zone.FlowId,
                    DetectionZone: {
                        DetectionZoneName: zone.DetectionZoneName,
                        DetectionZoneTypeId: zone.DetectionZoneTypeId,
                        DetectionZonePointArray: zone.DetectionZonePointArray,
                        DetectionZoneTripwirePoints: zone.DetectionZoneTripwirePoints ?? null,
                        DetectionZoneSettings: zone.DetectionZoneSettings
                    }
                }

                const response = await apiClientDB.post(`/detection_zones/add`, data, {
                    headers: {
                        'Authorization': `Bearer ${localStorage.getItem('authToken')}`
                    }
                })
                return response.data
            } catch (error) {
                console.error('Error adding detection zone:', error.response ? error.response.data : error.message)
                throw error // Rethrow error to be caught in the calling function
            }
        },
        
        // Replace an active zone with its next version in one server transaction —
        // see backend applicaite/zone_identity.py.
        async supersedeDetectionZone(oldZoneId, zone, changeKind = null, markerLabel = null) {
            const data = {
                // corrected (default) | changed | different — the editor's question.
                ...(changeKind ? { change_kind: changeKind } : {}),
                ...(changeKind === 'changed' && markerLabel ? { marker_label: markerLabel } : {}),
                DetectionZone: {
                    DetectionZoneName: zone.DetectionZoneName,
                    DetectionZoneTypeId: zone.DetectionZoneTypeId,
                    DetectionZonePointArray: zone.DetectionZonePointArray,
                    DetectionZoneTripwirePoints: zone.DetectionZoneTripwirePoints ?? null,
                    DetectionZoneSettings: zone.DetectionZoneSettings
                }
            }
            const response = await apiClientDB.post(`/detection_zones/${oldZoneId}/supersede/`, data, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('authToken')}`
                }
            })
            return response.data
        },

        // A chart marker for a zone that was not edited ("the camera was moved").
        async addZoneMarker(zoneKey, label) {
            const response = await apiClientDB.post('/zone_markers/', { ZoneKey: zoneKey, Label: label }, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('authToken')}`
                }
            })
            return response.data
        },

        // Soft-retire, not a hard delete: the row survives as the archive record of
        // the geometry that produced the events up to this point. `reason` tells the
        // backend whether a replacement row is coming ('superseded') or the zone is
        // simply gone ('deleted'); omitting it is safe and degrades to 'deleted'.
        async deleteDetectionZone(zoneId, reason = 'deleted') {
            const response = await apiClientDB.delete(`/detection_zones/${zoneId}/`, {
                params: { reason },
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('authToken')}`
                }
            });
            return response.data
        },


        async editDetectionZone(DetectionZoneId, DetectionZone) {
            try {
                const data = {
                    DetectionZone
                }
                await apiClientDB.put(`/detection_zones/${DetectionZoneId}/`, data, {
                    headers: {
                        'Authorization': `Bearer ${localStorage.getItem('authToken')}`
                    }
                })
                await this.fetchDetectionZone(DetectionZoneId)
            }
            catch (error) {
                this.error = error
            }
            finally {
                this.loading = false
            }
        },

        // `changeKind`: the answer when the save flips a counting direction (step 2e) —
        // changed adds a chart marker, different starts the zone fresh.
        // `matchChange`: a visit-matching setting changed — 'from_today' (the backend's
        // default) or 'correct_past' (the store's past year is recalculated tonight).
        async updateDetectionZoneSettings(flowId, detectionZoneName, updatedSettings, changeKind = null, matchChange = null) {
            try {
                const response = await apiClientDB.put(`/detection_zones/flow/${flowId}/zone/${encodeURIComponent(detectionZoneName)}/`, {
                    DetectionZoneSettings: JSON.stringify(updatedSettings),
                    ...(changeKind ? { change_kind: changeKind } : {}),
                    ...(matchChange ? { match_change: matchChange } : {}),
                },
                { 
                    headers: { 
                        'Authorization': `Bearer ${localStorage.getItem('authToken')}` 
                    } 
                }
                );
        
                return response.data;
            } catch (error) {
                console.error("Error updating detection zone settings:", error);
                throw error;
            }
        },

        clearDetectionZones() {
            // console.log('Removing DetectionZoneData')
            this.detectionzones = []
            // console.log('this.detectionzones:', this.detectionzones)
            localStorage.removeItem('detectionZoneData')  // Clear from localStorage
            // console.log('DetectionZoneData Removed')
        }
    }
})